import moment from "moment";
import * as React from "react";
import { useCookies } from "react-cookie";
import Utility from "../domain/classes/Utility";

// Enum
import JourneyStage from "../domain/enums/JourneyStage";
import IAppointmentBookingRequest, {
	InitialAppointmentBookingRequestState,
} from "../domain/interfaces/IAppointmentBookingRequest";
import IAppointmentBookingResponse, {
	InitialAppointmentBookingResponseState,
} from "../domain/interfaces/IAppointmentBookingResponse";
import IAppointmentType, {
	InitialAppointmentTypeState,
} from "../domain/interfaces/IAppointmentType";
import IAppointmentTypeProviderDto from "../domain/interfaces/IAppointmentTypeProviderDto";
import IConcessionCard, {
	InitialConcessionCardState,
} from "../domain/interfaces/IConcessionCard";
import IConcessionCardError, {
	InitialConcessionCardErrorState,
} from "../domain/interfaces/IConcessionCardError";
import IConditionalProgress from "../domain/interfaces/IConditionalProgress";
import IDoctor from "../domain/interfaces/IDoctor";

// Interfaces
import ILocation, {
	InitialLocationState,
} from "../domain/interfaces/ILocation";
import IMedicareCard, {
	InitialMedicareCardState,
} from "../domain/interfaces/IMedicareCard";
import IMedicareCardError, {
	InitialMedicareCardErrorState,
} from "../domain/interfaces/IMedicareCardError";
import IPatientDetails, {
	InitialPatientDetails,
} from "../domain/interfaces/IPatientDetails";
import IPatientValidationError, {
	InitialPatientValidationErrorState,
} from "../domain/interfaces/IPatientValidationError";

export interface IApplicationManagerContext {
	journeyStage: JourneyStage;
	handleJourneyStageChange: (val: JourneyStage) => void;
	conditionalProgress: IConditionalProgress;
	handleConditionalProgressChange: (val: IConditionalProgress) => void;
	journeyError: string;
	handleJourneyErrorChange: (val: string) => void;
	location?: ILocation;
	handleLocationChange: (val: ILocation) => void;
	selectedApptType: IAppointmentType;
	handleSelectedApptTypeChange: (val: IAppointmentType) => void;
	patientDetails: IPatientDetails;
	handlePatientDetailsChange: (
		key: keyof IPatientDetails,
		value: string | number | boolean
	) => void;
	patientValidationError: IPatientValidationError;
	handlePatientValidationErrorChange: (val: IPatientValidationError) => void;
	clearPatientValidationError: (key: keyof IPatientValidationError) => void;
	apptTypeOverrides: IAppointmentTypeProviderDto[];
	handleApptTypeOverridesChange: (val: IAppointmentTypeProviderDto[]) => void;
	selectedDoctor: IDoctor | null;
	handleSelectedDoctorChange: (doctor: IDoctor) => void;
	appointmentBookingRequest: IAppointmentBookingRequest;
	handleAppointmentBookingRequestChange: (
		data: IAppointmentBookingRequest
	) => void;
	stageResponse: IAppointmentBookingResponse;
	handleStageResponseChange: (val: IAppointmentBookingResponse) => void;
	medicareCard: IMedicareCard;
	handleMedicareCardChange: (card: IMedicareCard) => void;
	medicareCardError: IMedicareCardError;
	handleMedicareCardErrorChange: (cardError: IMedicareCardError) => void;
	concessionCard: IConcessionCard;
	handleConcessionCardChange: (card: IConcessionCard) => void;
	concessionCardError: IConcessionCardError;
	handleConcessionCardErrorChange: (card: IConcessionCardError) => void;
	clearApplicationData: () => void;
	clearCookies: () => void;
	dialog: React.ReactNode;
	pushDialog: (dialog: React.ReactNode) => void;
}

export const ApplicationContext = React.createContext<IApplicationManagerContext>(
	{
		journeyStage: JourneyStage.AppointmentTypeSelection,
		handleJourneyStageChange: (val: JourneyStage) => {},
		conditionalProgress: {},
		handleConditionalProgressChange: (val: IConditionalProgress) => {},
		journeyError: "",
		handleJourneyErrorChange: (val: string) => {},
		location: undefined,
		handleLocationChange: (val: ILocation) => {},
		selectedApptType: { ...InitialAppointmentTypeState },
		handleSelectedApptTypeChange: (val: IAppointmentType) => {},
		patientDetails: { ...InitialPatientDetails },
		handlePatientDetailsChange: (
			key: keyof IPatientDetails,
			value: string | number | boolean
		) => {},
		patientValidationError: { ...InitialPatientValidationErrorState },
		handlePatientValidationErrorChange: (
			val: IPatientValidationError
		) => {},
		clearPatientValidationError: (key: keyof IPatientValidationError) => {},
		apptTypeOverrides: [],
		handleApptTypeOverridesChange: (
			val: IAppointmentTypeProviderDto[]
		) => {},
		selectedDoctor: null,
		handleSelectedDoctorChange: (doctor: IDoctor) => {},
		appointmentBookingRequest: { ...InitialAppointmentBookingRequestState },
		handleAppointmentBookingRequestChange: (
			data: IAppointmentBookingRequest
		) => {},
		stageResponse: { ...InitialAppointmentBookingResponseState },
		handleStageResponseChange: (val: IAppointmentBookingResponse) => {},
		medicareCard: { ...InitialMedicareCardState },
		handleMedicareCardChange: (card: IMedicareCard) => {},
		medicareCardError: { ...InitialMedicareCardErrorState },
		handleMedicareCardErrorChange: (cardError: IMedicareCardError) => {},
		concessionCard: { ...InitialConcessionCardState },
		handleConcessionCardChange: (card: IConcessionCard) => {},
		concessionCardError: { ...InitialConcessionCardErrorState },
		handleConcessionCardErrorChange: (card: IConcessionCardError) => {},
		clearApplicationData: () => {},
		clearCookies: () => {},
		dialog: null,
		pushDialog: (dialog: React.ReactNode) => {}
	}
);

export interface ApplicationManagerProps {
	children: React.ReactNode;
}

const ApplicationManager: React.FC<ApplicationManagerProps> = ({
	children,
}) => {
	const [cookies, setCookies, removeCookies] = useCookies();

	// States
	const [journeyStage, setJourneyStage] = React.useState<JourneyStage>(
		JourneyStage.AppointmentTypeSelection
	);

	const [
		conditionalProgress,
		setConditionalProgress,
	] = React.useState<IConditionalProgress>({});

	const [error, setError] = React.useState<string>("");

	const [location, setLocation] = React.useState<ILocation>({
		...InitialLocationState,
	});

	const [
		selectedApptType,
		setSelectedApptType,
	] = React.useState<IAppointmentType>({ ...InitialAppointmentTypeState });

	const [patientDetails, setPatientDetails] = React.useState<IPatientDetails>(
		{
			...InitialPatientDetails,
		}
	);
	const [
		patientValidationError,
		setPatientValidationError,
	] = React.useState<IPatientValidationError>({
		...InitialPatientValidationErrorState,
	});

	const [apptTypeOverrides, setApptTypeOverrides] = React.useState<
		IAppointmentTypeProviderDto[]
	>([]);
	const [selectedDoctor, setSelectedDoctor] = React.useState<IDoctor | null>(
		null
	);
	const [
		appointmentBookingRequest,
		setAppointmentBookingRequest,
	] = React.useState<IAppointmentBookingRequest>({
		...InitialAppointmentBookingRequestState,
	});
	const [
		stageResponse,
		setStageResponse,
	] = React.useState<IAppointmentBookingResponse>({
		...InitialAppointmentBookingResponseState,
	});
	const [medicareCard, setMedicareCard] = React.useState<IMedicareCard>({
		...InitialMedicareCardState,
	});
	const [concessionCard, setConcessionCard] = React.useState<IConcessionCard>(
		{ ...InitialConcessionCardState }
	);
	const [
		medicareCardError,
		setMedicareCardError,
	] = React.useState<IMedicareCardError>({
		...InitialMedicareCardErrorState,
	});
	const [
		concessionCardError,
		setConcessionCardError,
	] = React.useState<IConcessionCardError>({
		...InitialConcessionCardErrorState,
	});

	// Effects

	// Handlers
	const handleJourneyStageChange = (val: JourneyStage) => {
		setJourneyStage(val);
	};

	const handleErrorChange = (val: string) => {
		setError(val);
	};

	const handleLocationChange = React.useCallback((val: ILocation) => {
		setLocation(val);
	}, []);

	const handleConditionalProgressChange = (val: IConditionalProgress) => {
		setConditionalProgress(val);
	};

	const handleSelectedApptTypeChange = (val: IAppointmentType) => {
		setSelectedApptType(val);
	};

	const handlePatientDetailsChange = React.useCallback(
		(key: keyof IPatientDetails, value: string | number | boolean) => {
			setPatientDetails((prevState: IPatientDetails) => ({
				...prevState,
				[key]: value,
			}));
		},
		[]
	);

	const handlePatientValidationErrorChange = React.useCallback(
		(val: IPatientValidationError) => {
			setPatientValidationError(val);
		},
		[]
	);

	const handleApptTypeOverridesChange = (
		val: IAppointmentTypeProviderDto[]
	) => {
		setApptTypeOverrides(val);
	};

	const handleSelectedDoctorChange = (doctor: IDoctor) => {
		setSelectedDoctor(doctor);
	};

	const handleAppointmentBookingRequestChange = React.useCallback(
		(data: IAppointmentBookingRequest) => {
			setAppointmentBookingRequest(data);
		},
		[]
	);

	const handleStageResponseChange = (val: IAppointmentBookingResponse) => {
		setStageResponse(val);
	};

	const clearPatientValidationError = (
		key: keyof IPatientValidationError
	) => {
		handlePatientValidationErrorChange({
			...patientValidationError,
			[key]: "",
		});
	};

	const handleMedicareCardChange = (card: IMedicareCard) => {
		setMedicareCard(card);
	};

	const handleConcessionCardChange = (card: IConcessionCard) => {
		setConcessionCard(card);
	};

	const handleMedicareCardErrorChange = (cardError: IMedicareCardError) => {
		setMedicareCardError(cardError);
	};

	const handleConcessionCardErrorChange = (
		cardError: IConcessionCardError
	) => {
		setConcessionCardError(cardError);
	};

	const clearApplicationData = () => {
		setJourneyStage(JourneyStage.AppointmentTypeSelection);
		setConditionalProgress({});
		setError("");
		setSelectedApptType({ ...InitialAppointmentTypeState });
		setPatientDetails({
			...InitialPatientDetails,
		});
		setPatientValidationError({ ...InitialPatientValidationErrorState });
		setSelectedDoctor(null);
		setAppointmentBookingRequest({
			...InitialAppointmentBookingRequestState,
		});
		setStageResponse({ ...InitialAppointmentBookingResponseState });
	};

	const clearCookies = () => {
		removeCookies("doctor");
		removeCookies("appointment_type");
		removeCookies("appointment_request");
		removeCookies("stage_id");
		removeCookies("client_secret");
	};

	const [dialog, setDialog] = React.useState<React.ReactNode>(null);

	const pushDialog = (dialog: React.ReactNode) => {
		setDialog(dialog);
	}

	// Use effects
	React.useEffect(() => {
		// Utility.debug("Patient details updated:", patientDetails);
	}, [patientDetails]);

	return (
		<ApplicationContext.Provider
			value={{
				journeyStage,
				handleJourneyStageChange,
				conditionalProgress,
				handleConditionalProgressChange,
				journeyError: error,
				handleJourneyErrorChange: handleErrorChange,
				location,
				handleLocationChange,
				selectedApptType,
				handleSelectedApptTypeChange,
				patientDetails,
				handlePatientDetailsChange,
				patientValidationError,
				handlePatientValidationErrorChange,
				apptTypeOverrides,
				handleApptTypeOverridesChange,
				selectedDoctor,
				handleSelectedDoctorChange,
				appointmentBookingRequest,
				handleAppointmentBookingRequestChange,
				stageResponse,
				handleStageResponseChange,
				medicareCard,
				handleMedicareCardChange,
				medicareCardError,
				handleMedicareCardErrorChange,
				concessionCard,
				handleConcessionCardChange,
				concessionCardError,
				handleConcessionCardErrorChange,
				clearPatientValidationError,
				clearApplicationData,
				clearCookies,
				dialog,
				pushDialog
			}}
		>
			{children}
		</ApplicationContext.Provider>
	);
};

export const useApplicationManager = () => React.useContext(ApplicationContext);

export default ApplicationManager;
